/**
 * ProseMirrorEditorInstance: Plain class that handles all ProseMirror-specific logic
 *
 * This class is completely decoupled from React and manages the EditorView lifecycle.
 * It provides a clean interface for initializing, managing, and destroying a ProseMirror
 * editor instance.
 *
 * Why a plain class instead of a React component?
 * - ProseMirror is an imperative library that manages its own DOM
 * - A class instance provides stable references and clear lifecycle management
 */

import { get, isEqual } from "lodash-es";
import { EditorState, type Transaction } from "prosemirror-state";
import { Mapping, Transform } from "prosemirror-transform";
import { EditorView } from "prosemirror-view";
import Delta from "quill-delta";
import type { Doc, Op } from "sharedb/lib/client";

import type {
  PresenceDataType,
  PresenceViewShortType,
  RichTextOpType,
} from "../../../../../shared/types/sharedb";
import type {
  PathType,
  RichTextCellType,
  WorksheetType,
} from "../../../../../shared/types/worksheet";
import type { UserWithSectionUserType } from "../../../../../store/selectors/types";
import { getUserColor } from "../../../../helpers/colors";

import {
  convertDeltaToProseMirrorDoc,
  convertDeltaToProseMirrorSteps,
  convertProseMirrorStepsToDelta,
  convertToDeltaPosition,
  convertToProseMirrorPosition,
  schema,
} from "./adapter";
import { linkPopoverPlugin } from "./plugins/link-popover";
import { placeholderPlugin } from "./plugins/placeholder";
import { UPDATE_REMOTE_SELECTIONS_META, remoteSelectionsPlugin } from "./plugins/selections";
import { createProseMirrorPlugins } from "./plugins/setup";
import type { ProseMirrorEditorInstanceOptions, RemoteSelectionData } from "./types";

import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { t } from "i18n/i18n";
import { logInfoWithDebugFlag, rollbarAndLogError, rollbarAndLogWarning } from "utils/logger";
import { getUserShortName } from "utils/user-utils";

/**
 * Delay (in milliseconds) before rendering remote selections to handle race condition
 */
const PRESENCE_RENDER_DELAY_MS = 300;

/**
 * ProseMirrorEditorInstance manages a ProseMirror EditorView lifecycle
 *
 * This class handles:
 * - Initializing the ProseMirror editor with schema and plugins
 * - Managing transactions and document changes
 * - ShareDB integration
 * - Cleanup and destruction
 */
export class ProseMirrorEditorInstance {
  private editorView: EditorView | null = null;

  private rootElement: HTMLElement;

  private sharedbDoc: Doc<WorksheetType>;

  private path: PathType;

  private readOnly: boolean;

  private editorState: EditorState;

  private userId?: string | number;

  private presenceViewType?: PresenceViewShortType;

  private users: UserWithSectionUserType[];

  private create: boolean;

  private embedded: boolean;

  private pageId?: string;

  private opHandler: ((ops: Op[], source: unknown) => void) | null = null;

  private presenceHandler: ((srcPresenceId: string, presence: PresenceDataType) => void) | null =
    null;

  // Maps ShareDB presence IDs to user IDs for tracking remote cursors
  private presenceIdMapping: Record<string, string> = {};

  // Stores remote selection data keyed by srcPresenceId
  private remoteSelectionsData: Map<string, RemoteSelectionData> = new Map();

  // Tracks active timeouts for rendering selections, keyed by userId
  // Stores timeout and updateCallback so we can execute updateCallback immediately when op arrives
  private remoteSelectionsTimeouts: Map<
    string,
    { timeout: ReturnType<typeof setTimeout>; updateCallback: () => void }
  > = new Map();

  /**
   * Workaround to filter out known erroneous or corrupt ops client-side.
   * Ops that are missing "insert" or have "retain" break the Delta/ProseMirror adapter. We filter
   * them out for display and, when editable, submit delete ops to fix the doc in ShareDB.
   */
  private removeInvalidOps(originalOps: RichTextCellType["ops"]): RichTextCellType["ops"] {
    // Collect indexes of ops that are either missing an "insert" key, or have a "retain" key
    const opIndexesToRemove = originalOps.reduce<number[]>((acc, op, index) => {
      if (op.insert == null || op.retain) {
        acc.push(index);
      }
      return acc;
    }, []);

    if (opIndexesToRemove.length === 0) return originalOps;

    const filteredOps = originalOps.filter((_, index) => !opIndexesToRemove.includes(index));

    rollbarAndLogWarning("[ProseMirrorEditor] Filtering non-insert ops client-side", {
      pageId: this.pageId,
      path: this.path,
      originalOps: JSON.stringify(originalOps),
      filteredOps: JSON.stringify(filteredOps),
      opIndexesToRemove,
    });

    if (!this.readOnly) {
      // Submit operations to delete the invalid ops
      // Delete in reverse order (highest index first) to prevent index shifting issues
      // when multiple deletions happen sequentially
      const removeOps = opIndexesToRemove
        .sort((a, b) => b - a)
        .map((i) => ({
          p: [...this.path, "ops", i],
          ld: originalOps[i],
          ldCheck: true as const, // custom flag to enable safety check to prevent deleting wrong element
        }));
      this.sharedbDoc.submitOp(removeOps, { source: this }, (err) => {
        if (err) {
          rollbarAndLogError(
            "[ProseMirrorEditor] Error submitting corrective ops",
            { pageId: this.pageId, path: this.path },
            err,
          );
        }
      });
    }

    return filteredOps;
  }

  constructor(rootElement: HTMLElement, options: ProseMirrorEditorInstanceOptions) {
    this.rootElement = rootElement;
    this.sharedbDoc = options.doc;
    this.path = options.path;
    this.readOnly = options.readOnly;
    this.userId = options.userId;
    this.presenceViewType = options.presenceViewType;
    this.users = options.users;
    this.create = options.create ?? false;
    this.embedded = options.embedded ?? false;
    this.pageId = options.pageId;

    // Load initial content from ShareDB doc
    const initialData = get(this.sharedbDoc.data, this.path) as RichTextCellType;
    const ops = initialData?.ops ?? undefined;
    const showToolbar = !this.readOnly && !this.embedded;
    const basePlugins = createProseMirrorPlugins(schema, showToolbar);
    const plugins = [
      ...basePlugins,
      placeholderPlugin({
        showPlaceholder: options.showPlaceholder ?? false,
        placeHolderText: options.placeHolderText?.trim() || t("worksheet.placeholder.content"),
        readOnly: this.readOnly,
      }),
      remoteSelectionsPlugin(),
      linkPopoverPlugin(schema),
    ];
    const emptyState = EditorState.create({ schema, plugins });
    let initialState = emptyState;

    if (ops && ops.length > 0) {
      const validOps = this.removeInvalidOps(ops);
      if (validOps.length > 0) {
        try {
          const proseMirrorDoc = convertDeltaToProseMirrorDoc(new Delta(validOps));
          initialState = EditorState.create({
            schema,
            doc: proseMirrorDoc,
            plugins,
          });
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          rollbarAndLogError(
            "[ProseMirrorEditor] Error initializing editor",
            { pageId: this.pageId, path: this.path, ops: validOps },
            error,
          );
        }
      }
    }

    this.editorState = initialState;

    // This attaches directly to the DOM element and manages its own DOM
    this.editorView = new EditorView(this.rootElement, {
      state: initialState as unknown as EditorState,
      editable: () => !this.readOnly,
      dispatchTransaction: (tr: Transaction) => {
        this.handleTransaction(tr as unknown as Transaction);
      },
    } as unknown as ConstructorParameters<typeof EditorView>[1]);

    this.editorView.dom.setAttribute("data-testid", "rich-text-editor");

    this.setupShareDB();
  }

  /**
   * Destroy the editor instance and clean up
   */
  destroy(): void {
    // Remove ShareDB listeners
    if (this.opHandler) {
      this.sharedbDoc.removeListener("op", this.opHandler);
      this.opHandler = null;
    }

    // Remove presence listeners
    if (this.presenceHandler) {
      this.sharedbDoc.presence.off("receive", this.presenceHandler);
      this.presenceHandler = null;
    }

    // Clear all pending remote selections timeouts
    for (const { timeout } of this.remoteSelectionsTimeouts.values()) {
      clearTimeout(timeout);
    }
    this.remoteSelectionsTimeouts.clear();

    // Destroy the editor view
    if (this.editorView) {
      this.editorView.destroy();
      this.editorView = null;
    }
  }

  /**
   * Sync editor contents from external doc.data changes (e.g., Timeline view, ShareDB error recovery, blocks moved)
   */
  syncContentsFromDoc(data: Delta, newPath: PathType): void {
    if (!this.editorView) return;

    this.path = newPath;

    const ops = data?.ops ?? undefined;
    const plugins = this.editorState.plugins;
    const emptyState = EditorState.create({ schema, plugins });
    let newState = emptyState;

    if (ops && ops.length > 0) {
      const validOps = this.removeInvalidOps(ops);
      if (validOps.length > 0) {
        try {
          const proseMirrorDoc = convertDeltaToProseMirrorDoc(new Delta(validOps));
          newState = EditorState.create({
            schema,
            doc: proseMirrorDoc,
            plugins,
          });
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          rollbarAndLogError(
            "[ProseMirrorEditor] Error syncing editor contents from ShareDB doc",
            { pageId: this.pageId, path: this.path, ops: validOps },
            error,
          );
          return;
        }
      }
    }

    // Update state and view directly (bypassing handleTransaction to avoid ShareDB submission)
    this.editorState = newState;
    this.editorView.updateState(this.editorState);
  }

  /**
   * Maps remote cursor positions through transaction mappings to keep them visually stable when
   * the document changes. Called for both local and remote document updates.
   *
   * ShareDB delivers presence (cursor positions) and ops (content changes) separately.
   * Without remapping, a doc change can make stored positions stale, causing remote cursors to jump
   * until the next presence update corrects them. Presence updates are authoritative and always
   * overwrite these mapped positions when they arrive.
   */
  private remapRemoteSelections(transactions: Transaction[]): void {
    if (this.remoteSelectionsData.size === 0 || transactions.length === 0) return;

    // Find the earliest position affected by any step across all transactions
    let earliestChangePos = Infinity;
    for (const tr of transactions) {
      for (const step of tr.steps) {
        if ("from" in step && typeof step.from === "number") {
          earliestChangePos = Math.min(earliestChangePos, step.from);
        }
      }
    }

    // No positional changes so no need to map selections
    if (earliestChangePos === Infinity) return;

    const combinedMapping = new Mapping();
    for (const tr of transactions) {
      combinedMapping.appendMapping(tr.mapping);
    }

    const updatedSelections = new Map<string, RemoteSelectionData>();
    for (const [srcPresenceId, selection] of this.remoteSelectionsData) {
      // Remap if selection extends to or past the change position
      // (selections entirely before the change don't need remapping)
      if (selection.to >= earliestChangePos) {
        const mappedFrom = combinedMapping.map(selection.from);
        const mappedTo = combinedMapping.map(selection.to);

        // Only update if positions changed (may collapse to a point if content was deleted)
        if (mappedFrom !== selection.from || mappedTo !== selection.to) {
          updatedSelections.set(srcPresenceId, { ...selection, from: mappedFrom, to: mappedTo });
        }
      }
    }

    // Only update view if selections were remapped
    if (updatedSelections.size > 0) {
      this.remoteSelectionsData = new Map([...this.remoteSelectionsData, ...updatedSelections]);
      this.renderRemoteSelections();
    }
  }

  /**
   * Handle ProseMirror transactions
   *
   * Transactions represent changes to the editor state. This method:
   * 1. Applies the transaction to get the new state
   * 2. Updates the view with the new state
   * 3. Handles any selection changes
   * 4. Submits changes to ShareDB
   */
  private handleTransaction(transaction: Transaction): void {
    if (!this.editorView) return;

    const newState = this.editorState.apply(transaction as unknown as Transaction);
    this.editorState = newState;
    this.editorView.updateState(this.editorState);

    // In readOnly mode, skip ShareDB operations but still apply transactions locally
    if (this.readOnly) return;

    if (transaction.docChanged) {
      // Keep remote cursors stable while local doc changes settle
      this.remapRemoteSelections([transaction]);

      // Submit document changes to ShareDB
      try {
        const delta = convertProseMirrorStepsToDelta(transaction);
        if (delta.ops.length === 0) return;

        const ops: (RichTextOpType | Op)[] = [
          {
            p: this.path,
            t: "rich-text",
            o: delta.ops,
            u: String(this.userId ?? ""),
          },
        ];

        // If create is true and the field doesn't exist, initialize it first
        if (this.create) {
          // This code ensures that pointing a ProseMirrorRichTextField to an empty data structure initializes
          // the structure before adding content data to it. Right now, this is only used for cells
          // in TableFields.
          // Path is either e.g. ['d', 'g', '<fieldId>', 'cm', '<rowId, colId>', 'v']
          // or             e.g. ['b', '0', 'f', '1', 'v', 'cm', '<rowId, colId>', 'v']
          // Note that this assumes that `cm` is initialized, and will overwrite any existing data
          // in cm.<rowId, colId> if data exists there (e.g. `ro` is set), but "v" doesn't.
          const currentData = get(this.sharedbDoc.data, this.path) as RichTextCellType;
          const pathToParent = this.path.slice(0, -1);

          if (!currentData) {
            // need to create the field, before we can modify it
            const lastPathPart = this.path[this.path.length - 1];
            ops.unshift({
              p: pathToParent,
              oi: { [lastPathPart]: {} },
            });
          }
        }

        logInfoWithDebugFlag("debug_prosemirror", "Submitting ops:", {
          pageId: this.pageId,
          path: this.path,
          ops,
        });

        // Include source to prevent feedback loops
        this.sharedbDoc.submitOp(ops, { source: this }, (err) => {
          if (err) {
            rollbarAndLogError(
              "[ProseMirrorEditor] Error submitting ops to ShareDB",
              { pageId: this.pageId, path: this.path },
              err,
            );
          }
        });
      } catch (e) {
        const error = e instanceof Error ? e : new Error(String(e));
        rollbarAndLogError(
          "[ProseMirrorEditor] Error processing local transaction",
          {
            pageId: this.pageId,
            path: this.path,
            steps: transaction.steps.map((s) => s.toJSON()),
            docChanged: transaction.docChanged,
            stepCount: transaction.steps.length,
          },
          error,
        );
      }
    }

    // Submit presence after op is queued to ensure position is correct relative to updated document
    if (transaction.selectionSet) this.submitPresenceUpdate();
  }

  /**
   * Set up ShareDB event listeners
   */
  private setupShareDB(): void {
    const _handleOp = (
      ops: RichTextOpType[],
      source: InstanceType<typeof ProseMirrorEditorInstance>,
    ) => {
      this.handleOp(ops, source);
    };

    const handlePresence = (srcPresenceId: string, presence: PresenceDataType) => {
      this.handlePresence(srcPresenceId, presence);
    };

    this.sharedbDoc.on("op", _handleOp);
    this.sharedbDoc.presence.on("receive", handlePresence);

    this.opHandler = _handleOp;
    this.presenceHandler = handlePresence;
  }

  /**
   * Submits presence update with current selection position to ShareDB
   */
  private submitPresenceUpdate(): void {
    if (
      !this.editorView ||
      this.readOnly ||
      !this.sharedbDoc.data ||
      !this.userId ||
      !this.presenceViewType
    )
      return;

    const selection = this.editorState.selection;

    try {
      const startQdPos = convertToDeltaPosition(this.editorState.doc, selection.from);
      const endQdPos = convertToDeltaPosition(this.editorState.doc, selection.to);

      logInfoWithDebugFlag("debug_prosemirror", "Submitting presence:", {
        pageId: this.pageId,
        path: this.path,
        pmFrom: selection.from,
        pmTo: selection.to,
        qdStart: startQdPos,
        qdEnd: endQdPos,
        empty: selection.empty,
      });

      // Submit presence to ShareDB
      this.sharedbDoc.localPresence.submit({
        p: this.path,
        t: "rich-text",
        v: this.presenceViewType,
        u: String(this.userId),
        s: {
          u: String(this.userId),
          s: [[startQdPos, endQdPos]],
        },
      });
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      rollbarAndLogError(
        "[ProseMirrorEditor] Error submitting presence",
        {
          pageId: this.pageId,
          path: this.path,
          userId: this.userId,
          pmFrom: selection.from,
          pmTo: selection.to,
        },
        error,
      );
    }
  }

  /**
   * Converts Quill Delta ops to ProseMirror steps and applies them
   */
  private handleOp(
    ops: RichTextOpType[],
    source: InstanceType<typeof ProseMirrorEditorInstance>,
  ): void {
    // Ignore our own submissions to prevent feedback loops
    if (source === this) return;

    // Check if any op targets our path
    if (!ops.some((op) => isEqual(op.p?.slice(0, this.path.length), this.path))) return;

    if (!this.editorView) return;

    logInfoWithDebugFlag("debug_prosemirror", "Processing remote ops:", {
      pageId: this.pageId,
      path: this.path,
      ops,
    });

    // Start with current state and apply each op as a transaction
    let newState = this.editorState;
    let docUpdated = false;
    const processedUserIds = new Set<string>();
    const appliedTransactions: Transaction[] = [];

    // Apply each op's steps sequentially using transactions
    for (const op of ops) {
      // Check if this op targets our exact path
      if (isEqual(op.p, this.path)) {
        // Track user ID for pending remote selections updates
        if (op.u) processedUserIds.add(op.u);

        // Convert Quill Delta ops to ProseMirror steps
        try {
          const deltaTransform = convertDeltaToProseMirrorSteps(
            new Transform(newState.doc),
            new Delta(op.o),
          );

          // Create a transaction from current state and apply all steps
          const pendingTransaction = newState.tr;
          for (const step of deltaTransform.steps) {
            pendingTransaction.step(step);
          }

          // Prevent remote ops from being added to the undo stack so that the user can't undo them
          pendingTransaction.setMeta("addToHistory", false);

          // Apply the transaction to get new state
          newState = newState.apply(pendingTransaction);
          appliedTransactions.push(pendingTransaction);
          docUpdated = true;
        } catch (e: unknown) {
          const error = e instanceof Error ? e : new Error(String(e));
          rollbarAndLogError(
            "[ProseMirrorEditor] Error updating editor state from remote ops",
            { pageId: this.pageId, path: this.path, op },
            error,
          );
          // Stop processing all ops - document might be in inconsistent state
          break;
        }
      }
    }

    // Only update if the document actually changed
    if (!docUpdated) return;

    // Update state and view directly (bypassing handleTransaction to avoid feedback loop)
    this.editorState = newState;
    this.editorView.updateState(this.editorState);

    // Keep remote cursors stable while remote doc changes settle
    this.remapRemoteSelections(appliedTransactions);

    // Check for pending selection updates for users whose ops we just processed
    // If presence arrived before the op, we can now process it immediately with updated document
    for (const userId of processedUserIds) {
      const pending = this.remoteSelectionsTimeouts.get(userId);
      if (pending) {
        clearTimeout(pending.timeout);
        this.remoteSelectionsTimeouts.delete(userId);
        pending.updateCallback();
      }
    }
  }

  /**
   * Renders the remote cursor and selections by dispatching a transaction
   */
  private renderRemoteSelections(): void {
    if (!this.editorView || !this.editorState) return;

    // Convert Map to array for plugin state
    const selectionsArray = Array.from(this.remoteSelectionsData.values());

    // Dispatch a transaction with metadata to update the plugin state
    const tr = this.editorState.tr;
    tr.setMeta(UPDATE_REMOTE_SELECTIONS_META, selectionsArray);
    this.editorView.dispatch(tr);
  }

  /**
   * Converts Quill Delta positions to ProseMirror positions and manages remote cursors
   */
  private handlePresence(srcPresenceId: string, presence: PresenceDataType): void {
    if (!this.editorView || !this.editorState) return;

    // We don't show presence on the projector view, because the scaling
    // interferes with the cursor placement, making it look buggy.
    if (isProjectorView()) return;

    // If no presence data, remove the cursor if it exists
    if (!presence) {
      if (this.presenceIdMapping[srcPresenceId]) {
        const userId = this.presenceIdMapping[srcPresenceId];
        this.remoteSelectionsData.delete(srcPresenceId);
        delete this.presenceIdMapping[srcPresenceId];
        this.renderRemoteSelections();
        logInfoWithDebugFlag("debug_prosemirror", "Removed cursor for disconnected user:", {
          pageId: this.pageId,
          path: this.path,
          userId,
        });
      }
      return;
    }

    const presenceUserId = presence.u;

    // Remove cursor if user is active in another field
    if (!isEqual(presence.p, this.path)) {
      if (this.presenceIdMapping[srcPresenceId]) {
        const userId = this.presenceIdMapping[srcPresenceId];
        this.remoteSelectionsData.delete(srcPresenceId);
        this.renderRemoteSelections();
        logInfoWithDebugFlag("debug_prosemirror", "Removed cursor for user in different field:", {
          pageId: this.pageId,
          path: this.path,
          userId,
        });
      }
      return;
    }

    // Filter presence based on view type
    // We only display presence coming from the same view as the current tab,
    // with the following exceptions:
    // - On the class responses tab, you can see presence from everyone
    // - Instructors editing the template can show presence on the student draft too
    if (this.presenceViewType) {
      const isClassResponses = this.presenceViewType === "c";
      const matchingPresenceView = presence.v === this.presenceViewType;
      const comesFromTemplate = presence.v === "t";
      const isDraftPage = this.presenceViewType === "d";
      if (!matchingPresenceView && !isClassResponses && !(comesFromTemplate && isDraftPage)) {
        return;
      }
    }

    // Validate presence type and structure
    if (presence.t !== "rich-text" || !presence.s) {
      rollbarAndLogError("[ProseMirrorEditor] Invalid remote presence data", {
        pageId: this.pageId,
        path: this.path,
        presence,
      });
      return;
    }

    // Update ID mapping
    this.presenceIdMapping[srcPresenceId] = presenceUserId;

    // Ignore our own presence or empty selections
    if (presenceUserId === this.userId || !presence.s.s || presence.s.s.length === 0) {
      return;
    }

    // Get the first selection
    // TODO: Support multiple selections
    const sel = presence.s.s[0];
    const len = Math.abs(sel[1] - sel[0]);
    const min = Math.min(sel[0], sel[1]);

    const startQdPos = min;
    const endQdPos = min + len;

    const username = getUserShortName(this.users.find((u) => u.id === presenceUserId) || null);
    const color = getUserColor(presenceUserId);
    const existingData = this.remoteSelectionsData.get(srcPresenceId);
    const isFirstRender = !existingData;

    logInfoWithDebugFlag("debug_prosemirror", "Processing remote presence:", {
      pageId: this.pageId,
      path: this.path,
      srcPresenceId,
      presenceUserId,
      username,
      qdStart: startQdPos,
      qdEnd: endQdPos,
      presenceViewType: this.presenceViewType,
    });

    const updateCallback = () => {
      try {
        const startPmPos = convertToProseMirrorPosition(this.editorState.doc, startQdPos);
        const endPmPos = convertToProseMirrorPosition(this.editorState.doc, endQdPos);

        const selectionData: RemoteSelectionData = {
          srcPresenceId,
          userId: presenceUserId,
          username,
          from: startPmPos,
          to: endPmPos,
          color,
          isFirstRender,
        };

        this.remoteSelectionsData.set(srcPresenceId, selectionData);
        this.renderRemoteSelections();
      } catch (e) {
        const error = e instanceof Error ? e : new Error(String(e));
        rollbarAndLogError(
          "[ProseMirrorEditor] Error processing remote presence",
          {
            pageId: this.pageId,
            path: this.path,
            srcPresenceId,
            presenceUserId,
            startQdPos,
            endQdPos,
          },
          error,
        );
      }
    };

    // If a previous pending update exists for this user, cancel its timeout
    const pending = this.remoteSelectionsTimeouts.get(presenceUserId);
    if (pending) {
      clearTimeout(pending.timeout);
    }

    // Delay rendering selections to handle race condition where presence arrives before ops.
    //
    // ShareDB limitation: Presence updates and document ops are sent separately
    // and there's no way to correlate them. When a user types or deletes, their presence
    // (cursor position) and the op (text insertion/deletion) may arrive in either order.
    //
    // Problem: If presence arrives first, we calculate cursor positions using stale document
    // state, causing the cursor to briefly appear on the wrong line before the op arrives
    // and corrects the document. This causes a visible flash as the cursor jumps.
    //
    // Solution: Store updateCallback keyed by userId. If an op arrives for that user, we execute
    // the updateCallback immediately with the updated document. Otherwise, the timeout fires as
    // a fallback for presence updates not associated with an op.
    const timeout = setTimeout(() => {
      this.remoteSelectionsTimeouts.delete(presenceUserId);
      updateCallback();
    }, PRESENCE_RENDER_DELAY_MS);

    this.remoteSelectionsTimeouts.set(presenceUserId, { timeout, updateCallback });
  }
}
